export const SUPPORTED_LOCALES = ['en', 'fr', 'es', 'it', 'de', 'pl', 'zh', 'ja', 'ko'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';
export const LOCALE_COOKIE = 'kraahl_locale';

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  de: 'Deutsch',
  pl: 'Polski',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
};

export const LOCALE_BCP47: Record<Locale, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  de: 'de-DE',
  pl: 'pl-PL',
  zh: 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
};

export function resolveLocale(raw?: string | null): Locale {
  if (raw && (SUPPORTED_LOCALES as readonly string[]).includes(raw)) {
    return raw as Locale;
  }
  return DEFAULT_LOCALE;
}

export function pickLocalized<T extends Partial<Record<Locale, string>> & { en: string }>(
  locale: Locale | undefined,
  strings: T
): string {
  const l = locale ?? DEFAULT_LOCALE;
  return strings[l] ?? strings.en;
}

/**
 * ISO 3166-1 alpha-2 country codes where French is an official or dominant language.
 * Used for geo-based locale detection via Vercel's x-vercel-ip-country header.
 */
export const FRENCH_SPEAKING_COUNTRIES = new Set([
  'FR', 'BE', 'CH', 'LU', 'MC', 'CA',
  'SN', 'CI', 'CM', 'BJ', 'BF', 'TG', 'ML', 'GN', 'NE',
  'TD', 'CF', 'CG', 'CD', 'GA', 'DJ', 'KM', 'MG', 'RW',
  'BI', 'MR', 'DZ', 'TN', 'MA', 'MU', 'SC', 'VU',
]);

export const SPANISH_SPEAKING_COUNTRIES = new Set([
  'ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU',
  'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR',
  'GQ', 'AD',
]);

export const ITALIAN_SPEAKING_COUNTRIES = new Set(['IT', 'SM', 'VA']);
